import { useRef, useState } from 'react'
import { useTaskboard } from '../lib/taskboardContext.js'
import useOutsideClose from '../lib/useOutsideClose.js'
import Avatar from './Avatar.jsx'

const menuItem = (active) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  width: '100%',
  textAlign: 'left',
  background: active ? 'rgba(200,168,75,.08)' : 'none',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  padding: '7px 9px',
  fontSize: 13,
  color: 'var(--color-white)',
  cursor: 'pointer',
})

export default function ProjectMembers({ project }) {
  const { currentUser, users, usersById, inviteTeammate, updateProjectMembers } = useTaskboard()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const ref = useRef(null)
  useOutsideClose(ref, open, () => setOpen(false))

  const memberUids = project.memberUids || []
  const members = memberUids.map((uid) => usersById[uid]).filter(Boolean)

  const toggleMember = (uid) => {
    if (uid === currentUser.uid) return // don't remove yourself (you'd lose access)
    const next = memberUids.includes(uid) ? memberUids.filter((x) => x !== uid) : [...memberUids, uid]
    updateProjectMembers(project.id, next)
  }

  const submitInvite = async () => {
    const e = email.trim()
    if (!e || busy) return
    setBusy(true)
    try {
      const id = await inviteTeammate(name, e)
      if (!memberUids.includes(id)) updateProjectMembers(project.id, [...memberUids, id])
      setName('')
      setEmail('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div ref={ref} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 26, position: 'relative' }}>
      <span className="tag-label" style={{ color: 'var(--color-muted)' }}>Members</span>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {members.map((u, i) => (
          <div key={u.uid} style={{ marginLeft: i === 0 ? 0 : -6 }} title={u.name + (u.pending ? ' (invited)' : '')}>
            <Avatar
              user={u}
              size={24}
              style={{ outline: '2px solid var(--color-panel-dark)', ...(u.pending ? { opacity: 0.5, filter: 'grayscale(.3)' } : {}) }}
            />
          </div>
        ))}
        {members.length === 0 && <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>Just you</span>}
      </div>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          background: 'none',
          border: '1px dashed var(--border-dash)',
          color: 'var(--color-muted)',
          borderRadius: 'var(--radius-sm)',
          fontSize: 10,
          padding: '4px 9px',
          cursor: 'pointer',
          letterSpacing: '.04em',
          textTransform: 'uppercase',
          fontFamily: 'var(--font-mono)',
        }}
      >
        + People
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 34,
            left: 64,
            width: 280,
            background: 'var(--color-panel-dark)',
            border: '1px solid var(--color-divider-dark)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)',
            padding: 10,
            zIndex: 25,
            animation: 'pop .12s ease',
          }}
        >
          <div className="eyebrow" style={{ marginBottom: 10 }}>
            <span className="tag-label" style={{ color: 'var(--color-gold)' }}>Project members</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 220, overflowY: 'auto', marginBottom: 10 }}>
            {users.map((u) => {
              const isMember = memberUids.includes(u.uid)
              const isSelf = u.uid === currentUser.uid
              return (
                <button key={u.uid} onClick={() => toggleMember(u.uid)} style={menuItem(isMember)} title={isSelf ? "You're always a member" : ''}>
                  <Avatar user={u} size={22} style={u.pending ? { opacity: 0.5 } : undefined} />
                  <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {u.name}
                    {u.pending && <span style={{ color: 'var(--text-dim)', fontSize: 10, marginLeft: 6, fontFamily: 'var(--font-mono)' }}>invited</span>}
                  </span>
                  {isMember && <span style={{ color: 'var(--color-gold)', fontSize: 13 }}>✓</span>}
                </button>
              )
            })}
          </div>

          <div style={{ borderTop: '1px solid var(--color-divider-dark)', paddingTop: 10 }}>
            <div className="tag-label" style={{ color: 'var(--color-muted)', marginBottom: 8 }}>Invite by email</div>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name (optional)"
              className="cn-input"
              style={{ fontSize: 12, padding: '7px 10px', marginBottom: 6 }}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitInvite()}
                placeholder="name@careernomad.org"
                className="cn-input"
                style={{ flex: 1, fontSize: 12, padding: '7px 10px' }}
              />
              <button onClick={submitInvite} disabled={busy} className="btn btn-gold" style={{ fontSize: 11, padding: '8px 12px', opacity: busy ? 0.5 : 1 }}>
                Invite
              </button>
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 7, lineHeight: 1.4 }}>
              They become assignable right away and link to their Google account on first sign-in.
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
